/** @module @liquidefy/code-editor/terminal/appearance/typography @version 1.0.0 @license LQD Research LLC */

/**
 * @rust terminal_appearance_typography.rs — false; 4/2/26; sm
 *
 * Terminal Typography System
 *
 * Maps brand typography to terminal-specific concerns.
 * Provides font configuration, cell measurement, grid computation,
 * glyph support detection, and CSS generation.
 * All functions are pure — no DOM, no side effects.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 TYPES
// ═══════════════════════════════════════════════════════════════════════════════

export interface TerminalTypography {
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly lineHeight: number;
  readonly letterSpacing: number;
  readonly fontWeight: number;
  readonly boldWeight: number;
  readonly cellWidth: number;
  readonly cellHeight: number;
  readonly ligatures: boolean;
  readonly fontFeatures: string[];
  readonly antialiasing: 'auto' | 'subpixel' | 'grayscale';
  readonly cursorStyle: 'block' | 'underline' | 'bar';
  readonly cursorBlink: boolean;
  readonly cursorWidth: number;
}

export interface FontMeasurement {
  readonly charWidth: number;
  readonly charHeight: number;
  readonly baseline: number;
  readonly ascent: number;
  readonly descent: number;
}

export type TerminalFontSize = 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl';

export interface GlyphSupport {
  readonly powerline: boolean;
  readonly nerdFont: boolean;
  readonly boxDrawing: boolean;
  readonly braille: boolean;
  readonly emoji: boolean;
}

export interface RecommendedFont {
  readonly name: string;
  readonly supports: GlyphSupport;
  readonly description: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §2 CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

/** Brand code font stack. */
const CODE_FONT_STACK = "'Berkeley Mono', 'JetBrains Mono', 'Fira Code', monospace";

/** Monospace character width ratio (character width / font size). */
const MONOSPACE_WIDTH_RATIO = 0.6;

/** Default OpenType features for code fonts. */
const DEFAULT_FONT_FEATURES: string[] = ['calt', 'liga'];

/** Font size presets mapping. */
const FONT_SIZE_MAP: Record<TerminalFontSize, number> = {
  xs: 11,
  sm: 12,
  base: 13,
  md: 14,
  lg: 16,
  xl: 20,
};

// ═══════════════════════════════════════════════════════════════════════════════
// §3 CELL DIMENSIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Estimate cell dimensions from font metrics.
 * Uses the 0.6 width ratio standard for monospace fonts.
 * Letter spacing is added to the cell width.
 */
export function computeCellDimensions(
  fontSize: number,
  lineHeight: number,
  letterSpacing: number,
): { width: number; height: number } {
  const width = fontSize * MONOSPACE_WIDTH_RATIO + letterSpacing;
  const height = fontSize * lineHeight;
  return { width: Math.round(width * 100) / 100, height: Math.round(height * 100) / 100 };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 createTerminalTypography — THE main function
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create default terminal typography using the brand code font stack.
 * Sensible defaults: 13px, 1.5 line height, ligatures enabled, block cursor.
 */
export function createTerminalTypography(fontSize?: number): TerminalTypography {
  const size = fontSize ?? 13;
  const lh = 1.5;
  const ls = 0;
  const cell = computeCellDimensions(size, lh, ls);

  return {
    fontFamily: CODE_FONT_STACK,
    fontSize: size,
    lineHeight: lh,
    letterSpacing: ls,
    fontWeight: 400,
    boldWeight: 700,
    cellWidth: cell.width,
    cellHeight: cell.height,
    ligatures: true,
    fontFeatures: [...DEFAULT_FONT_FEATURES],
    antialiasing: 'auto',
    cursorStyle: 'block',
    cursorBlink: true,
    cursorWidth: 2,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 FONT SIZE PRESETS
// ═══════════════════════════════════════════════════════════════════════════════

/** Map a named size preset to a pixel value. */
export function fontSizePreset(size: TerminalFontSize): number {
  return FONT_SIZE_MAP[size];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 TERMINAL GRID
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Compute how many columns and rows fit in a container.
 * Floors to whole cells so no partial characters are visible.
 */
export function computeTerminalGrid(
  containerWidth: number,
  containerHeight: number,
  typography: TerminalTypography,
): { cols: number; rows: number } {
  if (containerWidth <= 0 || containerHeight <= 0) return { cols: 0, rows: 0 };
  if (typography.cellWidth <= 0 || typography.cellHeight <= 0) return { cols: 0, rows: 0 };

  const cols = Math.floor(containerWidth / typography.cellWidth);
  const rows = Math.floor(containerHeight / typography.cellHeight);

  return { cols: Math.max(1, cols), rows: Math.max(1, rows) };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 CSS CUSTOM PROPERTIES
// ═══════════════════════════════════════════════════════════════════════════════

/** Generate CSS custom properties for terminal typography. */
export function typographyToCSS(typography: TerminalTypography): Record<string, string> {
  return {
    '--terminal-font-family': typography.fontFamily,
    '--terminal-font-size': `${typography.fontSize}px`,
    '--terminal-line-height': String(typography.lineHeight),
    '--terminal-letter-spacing': `${typography.letterSpacing}px`,
    '--terminal-font-weight': String(typography.fontWeight),
    '--terminal-bold-weight': String(typography.boldWeight),
    '--terminal-cell-width': `${typography.cellWidth}px`,
    '--terminal-cell-height': `${typography.cellHeight}px`,
    '--terminal-font-feature-settings': getFontFeatureString(typography),
    '--terminal-cursor-style': typography.cursorStyle,
    '--terminal-cursor-width': `${typography.cursorWidth}px`,
    '--terminal-cursor-blink': typography.cursorBlink ? 'blink 1s step-end infinite' : 'none',
    '--terminal-antialiasing': typography.antialiasing === 'subpixel'
      ? 'auto'
      : typography.antialiasing === 'grayscale'
        ? 'antialiased'
        : 'auto',
    '--terminal-webkit-smoothing': typography.antialiasing === 'subpixel'
      ? 'subpixel-antialiased'
      : typography.antialiasing === 'grayscale'
        ? 'antialiased'
        : 'auto',
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 TYPOGRAPHY MUTATORS — pure updaters returning new instances
// ═══════════════════════════════════════════════════════════════════════════════

/** Update font size and recompute cell dimensions. */
export function setFontSize(typography: TerminalTypography, fontSize: number): TerminalTypography {
  const clamped = Math.max(6, Math.min(72, fontSize));
  const cell = computeCellDimensions(clamped, typography.lineHeight, typography.letterSpacing);
  return { ...typography, fontSize: clamped, cellWidth: cell.width, cellHeight: cell.height };
}

/** Update line height and recompute cell dimensions. */
export function setLineHeight(typography: TerminalTypography, lineHeight: number): TerminalTypography {
  const clamped = Math.max(1.0, Math.min(3.0, lineHeight));
  const cell = computeCellDimensions(typography.fontSize, clamped, typography.letterSpacing);
  return { ...typography, lineHeight: clamped, cellWidth: cell.width, cellHeight: cell.height };
}

/** Enable or disable ligatures. Updates font features accordingly. */
export function enableLigatures(typography: TerminalTypography, enabled: boolean): TerminalTypography {
  const features = enabled
    ? Array.from(new Set([...typography.fontFeatures, 'calt', 'liga']))
    : typography.fontFeatures.filter(f => f !== 'calt' && f !== 'liga');

  return { ...typography, ligatures: enabled, fontFeatures: features };
}

/** Update cursor style. */
export function setCursorStyle(
  typography: TerminalTypography,
  style: TerminalTypography['cursorStyle'],
): TerminalTypography {
  return { ...typography, cursorStyle: style };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 GLYPH SUPPORT DETECTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Known font capabilities for heuristic glyph detection.
 * Based on published font documentation and common distributions.
 */
const FONT_CAPABILITIES: Record<string, GlyphSupport> = {
  'berkeley mono': {
    powerline: false,
    nerdFont: false,
    boxDrawing: true,
    braille: true,
    emoji: false,
  },
  'jetbrains mono': {
    powerline: true,
    nerdFont: false,
    boxDrawing: true,
    braille: true,
    emoji: false,
  },
  'fira code': {
    powerline: false,
    nerdFont: false,
    boxDrawing: true,
    braille: true,
    emoji: false,
  },
  'cascadia code': {
    powerline: false,
    nerdFont: false,
    boxDrawing: true,
    braille: true,
    emoji: false,
  },
  'cascadia mono': {
    powerline: false,
    nerdFont: false,
    boxDrawing: true,
    braille: true,
    emoji: false,
  },
  'hack': {
    powerline: false,
    nerdFont: false,
    boxDrawing: true,
    braille: true,
    emoji: false,
  },
  'iosevka': {
    powerline: false,
    nerdFont: false,
    boxDrawing: true,
    braille: true,
    emoji: false,
  },
  'source code pro': {
    powerline: false,
    nerdFont: false,
    boxDrawing: true,
    braille: true,
    emoji: false,
  },
  'menlo': {
    powerline: false,
    nerdFont: false,
    boxDrawing: true,
    braille: false,
    emoji: false,
  },
  'consolas': {
    powerline: false,
    nerdFont: false,
    boxDrawing: true,
    braille: false,
    emoji: false,
  },
  'sf mono': {
    powerline: false,
    nerdFont: false,
    boxDrawing: true,
    braille: true,
    emoji: false,
  },
};

/** Nerd Font variants append "Nerd Font" or "NF" to font names. */
const NERD_FONT_RE = /nerd\s*font|[\s-]nf$/i;

/**
 * Heuristic glyph support detection based on known font capabilities.
 * Checks the first recognized font in the stack.
 */
export function detectGlyphSupport(fontFamily: string): GlyphSupport {
  const normalized = fontFamily.toLowerCase().replace(/['"]/g, '').trim();

  // Check for Nerd Font variants
  if (NERD_FONT_RE.test(normalized)) {
    return {
      powerline: true,
      nerdFont: true,
      boxDrawing: true,
      braille: true,
      emoji: false,
    };
  }

  // Split font stack and check each font
  const fonts = normalized.split(',').map(f => f.trim());
  for (const font of fonts) {
    const clean = font.replace(/['"]/g, '');
    const match = FONT_CAPABILITIES[clean];
    if (match) return match;
  }

  // Generic monospace fallback
  return {
    powerline: false,
    nerdFont: false,
    boxDrawing: true,
    braille: false,
    emoji: false,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §10 RECOMMENDED FONTS
// ═══════════════════════════════════════════════════════════════════════════════

/** List of recommended terminal fonts with capabilities and descriptions. */
export function getRecommendedFonts(): RecommendedFont[] {
  return [
    {
      name: 'Berkeley Mono',
      supports: FONT_CAPABILITIES['berkeley mono']!,
      description: 'Premium monospace with excellent readability and box drawing support. The Liquidefy default.',
    },
    {
      name: 'JetBrains Mono',
      supports: FONT_CAPABILITIES['jetbrains mono']!,
      description: 'Free, open-source with built-in Powerline glyphs and extensive ligature set.',
    },
    {
      name: 'Fira Code',
      supports: FONT_CAPABILITIES['fira code']!,
      description: 'Popular free font with rich programming ligatures and wide Unicode coverage.',
    },
    {
      name: 'Cascadia Code',
      supports: FONT_CAPABILITIES['cascadia code']!,
      description: 'Microsoft open-source font with cursive italics and ligatures.',
    },
    {
      name: 'Iosevka',
      supports: FONT_CAPABILITIES['iosevka']!,
      description: 'Highly customizable, narrow monospace with many variants and styles.',
    },
    {
      name: 'Hack',
      supports: FONT_CAPABILITIES['hack']!,
      description: 'Clean, no-frills monospace optimized for source code legibility.',
    },
    {
      name: 'Source Code Pro',
      supports: FONT_CAPABILITIES['source code pro']!,
      description: 'Adobe open-source font designed for coding environments.',
    },
    {
      name: 'SF Mono',
      supports: FONT_CAPABILITIES['sf mono']!,
      description: 'Apple system monospace font with crisp rendering on macOS.',
    },
  ];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §11 FONT FEATURE STRING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generate CSS font-feature-settings value from typography config.
 * Produces strings like: "calt" 1, "liga" 1, "ss01" 1
 */
export function getFontFeatureString(typography: TerminalTypography): string {
  if (typography.fontFeatures.length === 0) return 'normal';
  return typography.fontFeatures.map(f => `"${f}" 1`).join(', ');
}

// ═══════════════════════════════════════════════════════════════════════════════
// §12 TYPOGRAPHY SUMMARY
// ═══════════════════════════════════════════════════════════════════════════════

/** Display summary of current typography settings. */
export function getTypographySummary(
  typography: TerminalTypography,
): Record<string, string | number | boolean> {
  return {
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize,
    lineHeight: typography.lineHeight,
    letterSpacing: typography.letterSpacing,
    fontWeight: typography.fontWeight,
    boldWeight: typography.boldWeight,
    cellWidth: typography.cellWidth,
    cellHeight: typography.cellHeight,
    ligatures: typography.ligatures,
    fontFeatures: typography.fontFeatures.join(', '),
    antialiasing: typography.antialiasing,
    cursorStyle: typography.cursorStyle,
    cursorBlink: typography.cursorBlink,
    cursorWidth: typography.cursorWidth,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §13 SCALE TYPOGRAPHY
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Scale font size by a factor and recompute all derived dimensions.
 * Factor of 1.0 = no change, 1.5 = 50% larger, 0.75 = 25% smaller.
 */
export function scaleTypography(typography: TerminalTypography, factor: number): TerminalTypography {
  const newSize = Math.round(typography.fontSize * Math.max(0.25, Math.min(4.0, factor)));
  return setFontSize(typography, newSize);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §14 FONT MEASUREMENT ESTIMATION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Estimate font measurements without DOM access.
 * Uses standard monospace proportions (ascent ~80%, descent ~20% of em).
 */
export function estimateFontMeasurement(fontSize: number, lineHeight: number): FontMeasurement {
  const charWidth = fontSize * MONOSPACE_WIDTH_RATIO;
  const charHeight = fontSize * lineHeight;
  const ascent = fontSize * 0.8;
  const descent = fontSize * 0.2;
  const baseline = ascent + (charHeight - fontSize) / 2;

  return {
    charWidth: Math.round(charWidth * 100) / 100,
    charHeight: Math.round(charHeight * 100) / 100,
    baseline: Math.round(baseline * 100) / 100,
    ascent: Math.round(ascent * 100) / 100,
    descent: Math.round(descent * 100) / 100,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §15 LETTER SPACING
// ═══════════════════════════════════════════════════════════════════════════════

/** Update letter spacing and recompute cell dimensions. */
export function setLetterSpacing(
  typography: TerminalTypography,
  letterSpacing: number,
): TerminalTypography {
  const clamped = Math.max(-2, Math.min(5, letterSpacing));
  const cell = computeCellDimensions(typography.fontSize, typography.lineHeight, clamped);
  return { ...typography, letterSpacing: clamped, cellWidth: cell.width, cellHeight: cell.height };
}

/** Update bold weight. */
export function setBoldWeight(
  typography: TerminalTypography,
  boldWeight: number,
): TerminalTypography {
  const clamped = Math.max(400, Math.min(900, boldWeight));
  return { ...typography, boldWeight: clamped };
}

/** Update antialiasing mode. */
export function setAntialiasing(
  typography: TerminalTypography,
  mode: TerminalTypography['antialiasing'],
): TerminalTypography {
  return { ...typography, antialiasing: mode };
}

/** Toggle cursor blink. */
export function setCursorBlink(
  typography: TerminalTypography,
  blink: boolean,
): TerminalTypography {
  return { ...typography, cursorBlink: blink };
}

/** Set bar cursor width in pixels. */
export function setCursorWidth(
  typography: TerminalTypography,
  width: number,
): TerminalTypography {
  return { ...typography, cursorWidth: Math.max(1, Math.min(4, width)) };
}
